import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  (typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

export const InstallButton = ({ className = "", label = "Install app" }) => {
  const [deferred, setDeferred] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else if (isIOS()) {
      setShowIosHelp(true);
    } else {
      // Desktop/Android before the event is ready — guide the user.
      setShowIosHelp(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-button"
        className={
          className ||
          "inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan/40 text-cyan font-heading font-bold uppercase tracking-wide text-xs hover:bg-cyan/10 transition-all"
        }
      >
        <Download size={15} /> {label}
      </button>

      {showIosHelp && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 px-4 pb-6"
          data-testid="install-help-modal"
          onClick={() => setShowIosHelp(false)}
        >
          <div
            className="w-full max-w-sm bg-surface border border-white/10 rounded-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-black uppercase tracking-tight text-lg">
                Add to Home Screen
              </h3>
              <button
                onClick={() => setShowIosHelp(false)}
                data-testid="install-help-close"
                className="text-slate-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <ol className="mt-4 space-y-3 text-sm text-slate-300">
              <li className="flex items-center gap-3">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-cyan/10 text-cyan shrink-0">
                  <Share size={15} />
                </span>
                Tap the <strong>Share</strong> button in your browser toolbar.
              </li>
              <li className="flex items-center gap-3">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-cyan/10 text-cyan shrink-0">
                  <Plus size={15} />
                </span>
                Choose <strong>Add to Home Screen</strong>.
              </li>
              <li className="flex items-center gap-3">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-cyan/10 text-cyan shrink-0 font-bold text-xs">
                  ✓
                </span>
                Tap <strong>Add</strong> — Thortek launches full-screen like a native app.
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
};
